import type {
  NavigationMode,
  SurfaceCapabilities,
  SurfaceNavTab,
} from "../../lib/surface-capabilities";
import pkgJson from "../../../package.json";

export const TOUR_STORAGE_KEY = "fintheon:tour-completed";
export const LAST_VERSION_KEY = "fintheon:last-seen-version";
export const CURRENT_VERSION = pkgJson.version ?? "0.0.0";

export type TourStepId =
  | "desk"
  | "consilium"
  | "scouts"
  | "riskflow"
  | "calendar"
  | "performance"
  | "settings"
  | "notifications"
  | "toolbar"
  | "decision-loop";

export type TourIconKey =
  | "desk"
  | "consilium"
  | "scouts"
  | "riskflow"
  | "calendar"
  | "performance"
  | "settings"
  | "notifications"
  | "toolbar"
  | "loop";

export interface TourStep {
  id: TourStepId;
  title: string;
  kicker: string;
  description: string;
  action: string;
  nav: SurfaceNavTab | null;
  selector: string;
  icon: TourIconKey;
  position: "top" | "bottom" | "left" | "right";
  primaryLabel: string;
  advanceOnTargetClick?: boolean;
  requiresTab?: SurfaceNavTab;
  requiresNavigationMode?: NavigationMode;
}

type BaseTourStep = Omit<TourStep, "primaryLabel">;

const BASE_TOUR_STEPS: BaseTourStep[] = [
  {
    id: "desk",
    title: "Desk",
    kicker: "Start here",
    description:
      "Read the active brief, scan desk plans, and keep Risk Signals near the decisions they affect.",
    action: "Click Desk any time you need to return to the operating picture.",
    nav: "dashboard",
    selector: 'button[data-tour-target="dashboard"]',
    icon: "desk",
    position: "right",
    advanceOnTargetClick: true,
  },
  {
    id: "consilium",
    title: "Consilium",
    kicker: "Research and CAO",
    description:
      "Use Harper, War Room, NarrativeFlow, FileRoom, and research tools from one analysis workspace.",
    action:
      "Click Consilium when you need a source-backed read or an approval-gated draft.",
    nav: "analysis",
    selector: 'button[data-tour-target="analysis"]',
    icon: "consilium",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "analysis",
  },
  {
    id: "scouts",
    title: "Scouts",
    kicker: "Automation loop",
    description:
      "Create recurring research jobs, route outputs into CAO context, and inspect run history before trusting a result.",
    action:
      "Click Scouts to build or review an automation without leaving the app shell.",
    nav: "scouts",
    selector: 'button[data-tour-target="scouts"]',
    icon: "scouts",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "scouts",
    requiresNavigationMode: "sidebar",
  },
  {
    id: "riskflow",
    title: "RiskFlow",
    kicker: "Live market feed",
    description:
      "Review source-linked market items, IV-weighted urgency, and signals before they become desk actions.",
    action:
      "Click RiskFlow to inspect headlines, sessions, and source context.",
    nav: "riskflow",
    selector: 'button[data-tour-target="riskflow"]',
    icon: "riskflow",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "riskflow",
  },
  {
    id: "calendar",
    title: "Calendar",
    kicker: "Catalyst timing",
    description:
      "Track economic events, earnings windows, and schedule risk with the full TradingView calendar surface.",
    action: "Click Calendar to check what can move the tape next.",
    nav: "econ",
    selector: 'button[data-tour-target="econ"]',
    icon: "calendar",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "econ",
    requiresNavigationMode: "sidebar",
  },
  {
    id: "performance",
    title: "Performance",
    kicker: "Trader development",
    description:
      "Review ProjectX activity, heatmaps, TradeDeveloper, and Holden Method reflections without mixing them into market research.",
    action:
      "Click Performance to separate execution review from live signal intake.",
    nav: "performance",
    selector: 'button[data-tour-target="performance"]',
    icon: "performance",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "performance",
  },
  {
    id: "settings",
    title: "Settings",
    kicker: "Provider and device control",
    description:
      "Manage OpenAI chat, automation providers, browsers, personalization, privacy, notifications, and desktop runtime settings.",
    action:
      "Click Settings when an integration or device capability needs attention.",
    nav: "settings",
    selector: 'button[data-tour-target="settings"]',
    icon: "settings",
    position: "right",
    advanceOnTargetClick: true,
    requiresTab: "settings",
    requiresNavigationMode: "sidebar",
  },
  {
    id: "notifications",
    title: "Notifications",
    kicker: "Signal control",
    description:
      "Use the bell to review queued alerts or toggle Do Not Disturb when the desk needs quiet.",
    action: "Click the bell to open queued notifications or change DND state.",
    nav: null,
    selector: 'button[data-tour-target="notifications"]',
    icon: "notifications",
    position: "right",
    advanceOnTargetClick: true,
    requiresNavigationMode: "sidebar",
  },
  {
    id: "toolbar",
    title: "Toolbar",
    kicker: "Fast controls",
    description:
      "Use the top rail for platform switching, panel layout, voice, chat, VIX, IV score, and browser controls.",
    action: "Scan the toolbar before assuming a capability is missing.",
    nav: null,
    selector: '[data-tour-target="toolbar"]',
    icon: "toolbar",
    position: "bottom",
  },
  {
    id: "decision-loop",
    title: "Daily Loop",
    kicker: "Use the system",
    description:
      "Start at Desk, validate through RiskFlow and Calendar, ask Consilium for a reviewed read, then save the outcome to memory.",
    action:
      "Return to Desk and use the app in that order when the market opens.",
    nav: "dashboard",
    selector: 'button[data-tour-target="dashboard"]',
    icon: "loop",
    position: "right",
  },
];

export const TOUR_STEPS: TourStep[] = getTourSteps();

export function getTourSteps(
  capabilities?: Pick<
    SurfaceCapabilities,
    "allowedTabs" | "navigationMode"
  > | null,
): TourStep[] {
  const steps = BASE_TOUR_STEPS.filter((step) => {
    if (
      step.requiresTab &&
      capabilities &&
      !capabilities.allowedTabs.includes(step.requiresTab)
    ) {
      return false;
    }

    if (
      step.requiresNavigationMode &&
      capabilities &&
      step.requiresNavigationMode !== capabilities.navigationMode
    ) {
      return false;
    }

    return true;
  });

  return steps.map((step, index) => ({
    ...step,
    primaryLabel:
      index === steps.length - 1 ? "Finish Tour" : getStepCta(steps[index + 1]),
  }));
}

function getStepCta(nextStep: BaseTourStep | undefined): string {
  if (!nextStep) return "Finish Tour";
  if (nextStep.id === "toolbar") return "Review Toolbar";
  if (nextStep.id === "decision-loop") return "Close the Loop";
  return `Open ${nextStep.title}`;
}

export const RELEASE_FEATURE_BRIEF = {
  kicker: "What Changed",
  title: `Updated to Epoch ${CURRENT_VERSION}`,
  intro:
    "This update makes the desk easier to read, sharper around live market context, and calmer during the daily decision loop.",
  items: [
    {
      category: "What Changed",
      title: "Sharper intraday levels",
      body: "Market context now does a better job separating levels that matter from levels that are just noise.",
      action:
        "Try it: open RiskFlow before your first setup and scan the levels before deciding whether price is near a meaningful area.",
    },
    {
      category: "What Changed",
      title: "Risk map adds rate context",
      body: "The map now gives a cleaner read on country risk, bond pressure, and interest-rate direction without making the view feel crowded.",
      action:
        "Try it: switch RiskFlow to Map and compare Risk, Bonds, and Rates before opening the feed.",
    },
    {
      category: "Agentic Desk Hardening",
      title: "Daily planning feels more aligned",
      body: "The desk is better at keeping the daily plan, live risk, and agent context pointed at the same session story.",
      action:
        "Try it: review Desk Plan beside RiskFlow before deciding whether the morning plan still fits.",
    },
    {
      category: "Stability and performance improvements",
      title: "Briefs and notifications stay calmer",
      body: "Daily briefs, update notices, and message status checks should feel more reliable and less noisy.",
      action:
        "Try it: check Dawn Dispatch after a scheduled window without forcing a refresh.",
    },
    {
      category: "What Changed",
      title: "The workspace is easier to scan",
      body: "RiskFlow, the map, mobile navigation, and dense desk controls have been polished so the important pieces sit closer to your eye.",
      action:
        "Try it: move between Desk and RiskFlow and notice how much less the chrome competes with the work.",
    },
  ],
};
